import React, { useState } from 'react';
import _ from 'lodash';
import {
  Button,
  Checkbox,
  CheckPicker,
  Col,
  FlexboxGrid,
  Form,
  InputNumber,
  Stack,
  Schema,
} from 'rsuite';
import { ItemDataType } from 'rsuite/CascadeTree';
import { useStoreActions, useStoreState } from '../../store/hooks';
import {
  getArrayIfNotEmpty,
  getBooleanValueOnlyIfTrue,
  getEmptyIfUndefined,
  getRangeIfNotEmpty,
  getValueIfNotEmpty,
} from '../../utils/filter';
import {
  PriceMode,
  PriceDecreasePossibility,
  ProductStates,
  PricingDataListFilter,
  PricingRequestStatus,
  PricingAlertPriority,
} from '../../api/apiTypes';

const model = Schema.Model({
  partnerProductCodes: Schema.Types.StringType()
    .pattern(/^[\w ]*[;\w ]+$/, 'SKUs must be separated by semicolon'),
});

function getPriceModeData(): ItemDataType[] {
  return Object
    .values(PriceMode)
    .map(mode => ({ value: mode, label: mode }));
}

function getPriceDecreasePossibilityData(): ItemDataType[] {
  return Object
    .values(PriceDecreasePossibility)
    .map(possibility => ({ value: possibility, label: possibility }));
}

function getProductStatesData(): ItemDataType[] {
  return Object
    .values(ProductStates)
    .map(state => ({ value: state, label: state }));
}

function getPricingRequestStatusData(): ItemDataType[] {
  return Object
    .values(PricingRequestStatus)
    .map(status => ({ value: status, label: status }));
}

function getAlertPrioritiesData(): ItemDataType[] {
  return Object
    .values(PricingAlertPriority)
    .map(priority => ({ value: priority, label: priority }));
}

interface Filter {
  priceModes: PriceMode[];
  vat: number | '';
  minOriginalB2bPrice: number | '';
  maxOriginalB2bPrice: number | '';
  minCalculatedCustomerPrice: number | '';
  maxCalculatedCustomerPrice: number | '';
  minCalculatedMinimumCustomerPrice: number | '';
  maxCalculatedMinimumCustomerPrice: number | '';
  minPartnerMinAbsoluteMargin: number | '';
  maxPartnerMinAbsoluteMargin: number | '';
  minPartnerMinRelativeMargin: number | '';
  maxPartnerMinRelativeMargin: number | '';
  minPartnerRegularAbsoluteMargin: number | '';
  maxPartnerRegularAbsoluteMargin: number | '';
  minPartnerRegularRelativeMargin: number | '';
  maxPartnerRegularRelativeMargin: number | '';
  minBasePriceInMarketplaceCurrency: number | '';
  maxBasePriceInMarketplaceCurrency: number | '';
  minScCategoryRelativeCommission: number | '';
  maxScCategoryRelativeCommission: number | '';
  minMpRelativeCommissionWithVat: number | '';
  maxMpRelativeCommissionWithVat: number | '';
  minMpRelativeCommissionWithoutVat: number | '';
  maxMpRelativeCommissionWithoutVat: number | '';
  competitionCheaperOnMarketplace: boolean | '';
  marketCompetitionCheaper: boolean | '';
  priceDecreasePossibilities: PriceDecreasePossibility[];
  partnerProductCodes: string;
  ean: string;
  eshopProductCode: string;
  eshopProductStatuses: ProductStates[];
  minHandlingTime: number | '';
  maxHandlingTime: number | '';
  onHold: boolean | '';
  minStock: number | '';
  maxStock: number | '';
  minPrice: number | '';
  maxPrice: number | '';
  minRecommendedRetailPrice: number | '';
  maxRecommendedRetailPrice: number | '';
  minForcedPrice: number | '';
  maxForcedPrice: number | '';
  brand: string;
  supplier: string;
  customField: string;
  categoryId: string;
  pricingRequestStatuses: PricingRequestStatus[];
  alertPriorities: PricingAlertPriority[];
}

const initialFilterValue: Filter = {
  partnerProductCodes: '',
  ean: '',
  brand: '',
  supplier: '',
  customField: '',
  categoryId: '',
  minStock: '',
  maxStock: '',
  competitionCheaperOnMarketplace: '',
  marketCompetitionCheaper: '',
  onHold: '',
  priceModes: [],
  vat: '',
  minOriginalB2bPrice: '',
  maxOriginalB2bPrice: '',
  minCalculatedCustomerPrice: '',
  maxCalculatedCustomerPrice: '',
  minCalculatedMinimumCustomerPrice: '',
  maxCalculatedMinimumCustomerPrice: '',
  minPartnerMinAbsoluteMargin: '',
  maxPartnerMinAbsoluteMargin: '',
  minPartnerMinRelativeMargin: '',
  maxPartnerMinRelativeMargin: '',
  minPartnerRegularAbsoluteMargin: '',
  maxPartnerRegularAbsoluteMargin: '',
  minPartnerRegularRelativeMargin: '',
  maxPartnerRegularRelativeMargin: '',
  minBasePriceInMarketplaceCurrency: '',
  maxBasePriceInMarketplaceCurrency: '',
  minScCategoryRelativeCommission: '',
  maxScCategoryRelativeCommission: '',
  minMpRelativeCommissionWithVat: '',
  maxMpRelativeCommissionWithVat: '',
  minMpRelativeCommissionWithoutVat: '',
  maxMpRelativeCommissionWithoutVat: '',
  eshopProductCode: '',
  minRecommendedRetailPrice: '',
  maxRecommendedRetailPrice: '',
  minForcedPrice: '',
  maxForcedPrice: '',
  priceDecreasePossibilities: [],
  eshopProductStatuses: [],
  minHandlingTime: '',
  maxHandlingTime: '',
  minPrice: '',
  maxPrice: '',
  pricingRequestStatuses: [],
  alertPriorities: [],
};

interface Props {
  visible: boolean;
}

function mapInternalFilterToApiFilter(
  marketplaceCodes: string[],
  filter: Filter,
): PricingDataListFilter {
  return {
    marketplaceCodes,
    priceModes: getArrayIfNotEmpty(filter.priceModes),
    vat: getValueIfNotEmpty(filter.vat),
    originalB2bPriceRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minOriginalB2bPrice),
      getValueIfNotEmpty(filter.maxOriginalB2bPrice),
    ),
    calculatedCustomerPriceRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minCalculatedCustomerPrice),
      getValueIfNotEmpty(filter.maxCalculatedCustomerPrice),
    ),
    calculatedMinimumCustomerPriceRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minCalculatedMinimumCustomerPrice),
      getValueIfNotEmpty(filter.maxCalculatedMinimumCustomerPrice),
    ),
    partnerMinAbsoluteMarginRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minPartnerMinAbsoluteMargin),
      getValueIfNotEmpty(filter.maxPartnerMinAbsoluteMargin),
    ),
    partnerMinRelativeMarginRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minPartnerMinRelativeMargin),
      getValueIfNotEmpty(filter.maxPartnerMinRelativeMargin),
    ),
    partnerRegularAbsoluteMarginRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minPartnerRegularAbsoluteMargin),
      getValueIfNotEmpty(filter.maxPartnerRegularAbsoluteMargin),
    ),
    partnerRegularRelativeMarginRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minPartnerRegularRelativeMargin),
      getValueIfNotEmpty(filter.maxPartnerRegularRelativeMargin),
    ),
    basePriceInMarketplaceCurrencyRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minBasePriceInMarketplaceCurrency),
      getValueIfNotEmpty(filter.maxBasePriceInMarketplaceCurrency),
    ),
    scCategoryRelativeCommissionRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minScCategoryRelativeCommission),
      getValueIfNotEmpty(filter.maxScCategoryRelativeCommission),
    ),
    mpRelativeCommissionWithVatRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minMpRelativeCommissionWithVat),
      getValueIfNotEmpty(filter.maxMpRelativeCommissionWithVat),
    ),
    mpRelativeCommissionWithoutVatRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minMpRelativeCommissionWithoutVat),
      getValueIfNotEmpty(filter.maxMpRelativeCommissionWithoutVat),
    ),
    competitionCheaperOnMarketplace: getBooleanValueOnlyIfTrue(
      filter.competitionCheaperOnMarketplace,
    ),
    marketCompetitionCheaper: getBooleanValueOnlyIfTrue(filter.marketCompetitionCheaper),
    priceDecreasePossibilities: getArrayIfNotEmpty(filter.priceDecreasePossibilities),
    partnerProductCodes: getArrayIfNotEmpty(_.compact(_.split(filter.partnerProductCodes, ';'))),
    ean: getValueIfNotEmpty(filter.ean),
    eshopProductCode: getValueIfNotEmpty(filter.eshopProductCode),
    eshopProductStatuses: getArrayIfNotEmpty(filter.eshopProductStatuses),
    handlingTimeRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minHandlingTime),
      getValueIfNotEmpty(filter.maxHandlingTime),
    ),
    onHold: getBooleanValueOnlyIfTrue(filter.onHold),
    stockRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minStock),
      getValueIfNotEmpty(filter.maxStock),
    ),
    priceRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minPrice),
      getValueIfNotEmpty(filter.maxPrice),
    ),
    recommendedRetailPriceRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minRecommendedRetailPrice),
      getValueIfNotEmpty(filter.maxRecommendedRetailPrice),
    ),
    forcedPriceRange: getRangeIfNotEmpty(
      getValueIfNotEmpty(filter.minForcedPrice),
      getValueIfNotEmpty(filter.maxForcedPrice),
    ),
    brand: getValueIfNotEmpty(filter.brand),
    supplier: getValueIfNotEmpty(filter.supplier),
    customField: getValueIfNotEmpty(filter.customField),
    categoryId: getValueIfNotEmpty(filter.categoryId),
    pricingRequestStatuses: getArrayIfNotEmpty(filter.pricingRequestStatuses),
    alertPriorities: getArrayIfNotEmpty(filter.alertPriorities),
  };
}

function mapApiFilterToInternalFilter(filter: PricingDataListFilter): Filter {
  return {
    priceModes: filter.priceModes ?? [],
    vat: getEmptyIfUndefined(filter.vat),
    minOriginalB2bPrice: getEmptyIfUndefined(filter.originalB2bPriceRange?.from),
    maxOriginalB2bPrice: getEmptyIfUndefined(filter.originalB2bPriceRange?.to),
    minCalculatedCustomerPrice: getEmptyIfUndefined(filter.calculatedCustomerPriceRange?.from),
    maxCalculatedCustomerPrice: getEmptyIfUndefined(filter.calculatedCustomerPriceRange?.to),
    minCalculatedMinimumCustomerPrice: getEmptyIfUndefined(
      filter.calculatedMinimumCustomerPriceRange?.from,
    ),
    maxCalculatedMinimumCustomerPrice: getEmptyIfUndefined(
      filter.calculatedMinimumCustomerPriceRange?.to,
    ),
    minPartnerMinAbsoluteMargin: getEmptyIfUndefined(filter.partnerMinAbsoluteMarginRange?.from),
    maxPartnerMinAbsoluteMargin: getEmptyIfUndefined(filter.partnerMinAbsoluteMarginRange?.to),
    minPartnerMinRelativeMargin: getEmptyIfUndefined(filter.partnerMinRelativeMarginRange?.from),
    maxPartnerMinRelativeMargin: getEmptyIfUndefined(filter.partnerMinRelativeMarginRange?.to),
    minPartnerRegularAbsoluteMargin: getEmptyIfUndefined(
      filter.partnerRegularAbsoluteMarginRange?.from,
    ),
    maxPartnerRegularAbsoluteMargin: getEmptyIfUndefined(
      filter.partnerRegularAbsoluteMarginRange?.to,
    ),
    minPartnerRegularRelativeMargin: getEmptyIfUndefined(
      filter.partnerRegularRelativeMarginRange?.from,
    ),
    maxPartnerRegularRelativeMargin: getEmptyIfUndefined(
      filter.partnerRegularRelativeMarginRange?.to,
    ),
    minBasePriceInMarketplaceCurrency: getEmptyIfUndefined(
      filter.basePriceInMarketplaceCurrencyRange?.from,
    ),
    maxBasePriceInMarketplaceCurrency: getEmptyIfUndefined(
      filter.basePriceInMarketplaceCurrencyRange?.to,
    ),
    minScCategoryRelativeCommission: getEmptyIfUndefined(
      filter.scCategoryRelativeCommissionRange?.from,
    ),
    maxScCategoryRelativeCommission: getEmptyIfUndefined(
      filter.scCategoryRelativeCommissionRange?.to,
    ),
    minMpRelativeCommissionWithVat: getEmptyIfUndefined(
      filter.mpRelativeCommissionWithVatRange?.from,
    ),
    maxMpRelativeCommissionWithVat: getEmptyIfUndefined(
      filter.mpRelativeCommissionWithVatRange?.to,
    ),
    minMpRelativeCommissionWithoutVat: getEmptyIfUndefined(
      filter.mpRelativeCommissionWithoutVatRange?.from,
    ),
    maxMpRelativeCommissionWithoutVat: getEmptyIfUndefined(
      filter.mpRelativeCommissionWithoutVatRange?.to,
    ),
    competitionCheaperOnMarketplace: getEmptyIfUndefined(filter.competitionCheaperOnMarketplace),
    marketCompetitionCheaper: getEmptyIfUndefined(filter.marketCompetitionCheaper),
    priceDecreasePossibilities: filter.priceDecreasePossibilities ?? [],
    partnerProductCodes: filter.partnerProductCodes ? _.join(filter.partnerProductCodes, ';') : '',
    ean: getEmptyIfUndefined(filter.ean),
    eshopProductCode: getEmptyIfUndefined(filter.eshopProductCode),
    eshopProductStatuses: filter.eshopProductStatuses ?? [],
    minHandlingTime: getEmptyIfUndefined(filter.handlingTimeRange?.from),
    maxHandlingTime: getEmptyIfUndefined(filter.handlingTimeRange?.to),
    onHold: getEmptyIfUndefined(filter.onHold),
    minStock: getEmptyIfUndefined(filter.stockRange?.from),
    maxStock: getEmptyIfUndefined(filter.stockRange?.to),
    minPrice: getEmptyIfUndefined(filter.priceRange?.from),
    maxPrice: getEmptyIfUndefined(filter.priceRange?.to),
    minRecommendedRetailPrice: getEmptyIfUndefined(filter.recommendedRetailPriceRange?.from),
    maxRecommendedRetailPrice: getEmptyIfUndefined(filter.recommendedRetailPriceRange?.to),
    minForcedPrice: getEmptyIfUndefined(filter.forcedPriceRange?.from),
    maxForcedPrice: getEmptyIfUndefined(filter.forcedPriceRange?.to),
    brand: getEmptyIfUndefined(filter.brand),
    supplier: getEmptyIfUndefined(filter.supplier),
    customField: getEmptyIfUndefined(filter.customField),
    categoryId: getEmptyIfUndefined(filter.categoryId),
    pricingRequestStatuses: filter.pricingRequestStatuses ?? [],
    alertPriorities: filter.alertPriorities ?? [],
  };
}

function onFormChange(
  value: Filter,
  setFormValue: React.Dispatch<React.SetStateAction<Filter>>,
) {
  setFormValue(value);
}

function applyFilter(
  formValue: Filter,
  currentFilter: { filter: PricingDataListFilter; page: number },
  setFilter: (payload: any) => void,
) {
  const marketplaceCodes = currentFilter.filter.marketplaceCodes || [];
  const mappedApiFilter = mapInternalFilterToApiFilter(
    marketplaceCodes,
    formValue,
  );

  setFilter({
    ...currentFilter,
    page: 1,
    filter: mappedApiFilter,
  });
}

function resetFilter(
  currentFilter: { filter: PricingDataListFilter; page: number },
  setFilter: (payload: any) => void,
  setFormValue: React.Dispatch<React.SetStateAction<Filter>>,
) {
  setFormValue(initialFilterValue);
  const marketplaceCodes = currentFilter.filter.marketplaceCodes || [];
  const mappedApiFilter = mapInternalFilterToApiFilter(
    marketplaceCodes,
    initialFilterValue,
  );
  setFilter({
    ...currentFilter,
    page: 1,
    filter: mappedApiFilter,
  });
}

export default function PricingDataFilter({ visible }: Readonly<Props>) {
  const { setFilter } = useStoreActions(actions => actions.pricingData);
  const currentFilter = useStoreState(state => state.pricingData.currentFilter);

  const [formValue, setFormValue] = useState<Filter>(
    mapApiFilterToInternalFilter(currentFilter.filter),
  );

  if (!visible) {
    return null;
  }

  return (
    <div className="mt-20 w100p">
      <Form
        fluid
        layout="vertical"
        model={model}
        formValue={formValue}
        onChange={(value) => onFormChange(value as Filter, setFormValue)}
      >
        <FlexboxGrid align="middle" justify="start" className="flex-box-filters mt-10">
          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>EAN</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="ean" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>SKU</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="partnerProductCodes" placeholder="Semicolon separated SKUs" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>MP ID</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="eshopProductCode" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Offer Status</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control
              name="eshopProductStatuses"
              accepter={CheckPicker}
              data={getProductStatesData()}
            />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Partner Brand</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="brand" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Partner Supplier</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="supplier" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Partner Custom Field</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="customField" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Category ID</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="categoryId" />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Handling Time</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minHandlingTime" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxHandlingTime" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Stock</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minStock" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxStock" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>VAT (%)</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="vat" accepter={InputNumber} />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Partner B2C Price</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minPrice" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxPrice" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>RRP</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minRecommendedRetailPrice" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxRecommendedRetailPrice" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Force Price</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minForcedPrice" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxForcedPrice" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Calc Customer Price</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minCalculatedCustomerPrice" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxCalculatedCustomerPrice" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            Calc Minimum Customer Price
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minCalculatedMinimumCustomerPrice" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxCalculatedMinimumCustomerPrice" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            Partner Min Margin w.o. VAT
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minPartnerMinAbsoluteMargin" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxPartnerMinAbsoluteMargin" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Partner Min Margin (%)</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minPartnerMinRelativeMargin" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxPartnerMinRelativeMargin" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            Partner Regular Margin w.o. VAT
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minPartnerRegularAbsoluteMargin" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxPartnerRegularAbsoluteMargin" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            Partner Regular Margin (%)
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minPartnerRegularRelativeMargin" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxPartnerRegularRelativeMargin" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>B2B Price</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minBasePriceInMarketplaceCurrency" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxBasePriceInMarketplaceCurrency" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            SC Category Commissions (%)
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minScCategoryRelativeCommission" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxScCategoryRelativeCommission" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            MP Category Commission (%)
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minMpRelativeCommissionWithVat" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxMpRelativeCommissionWithVat" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            MP Category Commission (%) w.o. VAT
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minMpRelativeCommissionWithoutVat" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxMpRelativeCommissionWithoutVat" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>
            Possible to Decrease Price
          </FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control
              name="priceDecreasePossibilities"
              accepter={CheckPicker}
              data={getPriceDecreasePossibilityData()}
            />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Price Mode</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control name="priceModes" accepter={CheckPicker} data={getPriceModeData()} />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Pricing Request Status</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control
              name="pricingRequestStatuses"
              accepter={CheckPicker}
              data={getPricingRequestStatusData()}
            />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Alert Priorities</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Form.Control
              name="alertPriorities"
              accepter={CheckPicker}
              data={getAlertPrioritiesData()}
            />
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8}>Original B2B Price</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16}>
            <Stack spacing={5} alignItems="center">
              <Form.Control name="minOriginalB2bPrice" accepter={InputNumber} placeholder="From" />
              <span>-</span>
              <Form.Control name="maxOriginalB2bPrice" accepter={InputNumber} placeholder="To" />
            </Stack>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={3} colspan={8} />
          <FlexboxGrid.Item as={Col} lg={5} colspan={16} />

          <FlexboxGrid.Item as={Col} lg={3} colspan={8} />
          <FlexboxGrid.Item as={Col} lg={5} colspan={16} />

          <FlexboxGrid.Item as={Col} lg={3} colspan={8} />
          <FlexboxGrid.Item as={Col} lg={5} colspan={16} />

          <FlexboxGrid.Item as={Col} lg={3} colspan={8} className="mt-10">Flags</FlexboxGrid.Item>
          <FlexboxGrid.Item as={Col} lg={5} colspan={16} className="mt-10">
            <Form.Control
              name="onHold"
              accepter={Checkbox}
              checked={!!formValue.onHold}
              value={!formValue.onHold as any}
            >
              On Hold
            </Form.Control>
            <Form.Control
              name="marketCompetitionCheaper"
              accepter={Checkbox}
              checked={!!formValue.marketCompetitionCheaper}
              value={!formValue.marketCompetitionCheaper as any}
            >
              Competition Cheaper Market
            </Form.Control>
            <Form.Control
              name="competitionCheaperOnMarketplace"
              accepter={Checkbox}
              checked={!!formValue.competitionCheaperOnMarketplace}
              value={!formValue.competitionCheaperOnMarketplace as any}
            >
              Competition Cheaper MP
            </Form.Control>
          </FlexboxGrid.Item>

          <FlexboxGrid.Item as={Col} lg={16} colspan={24} />

          <FlexboxGrid.Item as={Col} lg={24} colspan={24}>
            <Stack spacing={10} className="mt-20" justifyContent="flex-start">
              <Button
                appearance="primary"
                onClick={() => applyFilter(formValue, currentFilter, setFilter)}
              >
                Filter
              </Button>
              <Button
                appearance="primary"
                onClick={() => resetFilter(currentFilter, setFilter, setFormValue)}
              >
                Clear
              </Button>
            </Stack>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Form>
    </div>
  );
}
